/*
 * Назначение: вкладка «Каналы» в настройках проекта.
 * Функционал: вкл/выкл и токены каналов «Сайт», «Telegram», «MAX».
 * Для сайта показывает два независимых сниппета:
 *   1) виджет-кнопка в углу — одной строкой <script> в <head> сайта;
 *   2) встроенный чат прямо в вёрстку — <div data-ai-seller-inline>
 *      ставится на конкретной странице, скрипт повторно подключать не надо.
 * Оба сниппета сосуществуют: на странице с <div> отрисуется и встроенный
 * чат, и кнопка в углу.
 */

import { useEffect, useState } from "react";
import { Copy, Loader2, Save } from "lucide-react";
import { toast } from "sonner";

import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import type { ProjectDetails, ProjectUpdate } from "../../../lib/types";

import { useProjectMutation } from "./shared";

export function ChannelsTab({ project }: { project: ProjectDetails }) {
  const mutation = useProjectMutation("сохранить настройки каналов");
  const [form, setForm] = useState({
    web_enabled: project.web_enabled,
    telegram_enabled: project.telegram_enabled,
    telegram_bot_token: "",
    max_enabled: project.max_enabled,
    max_bot_token: "",
  });

  useEffect(() => {
    setForm((s) => ({
      ...s,
      web_enabled: project.web_enabled,
      telegram_enabled: project.telegram_enabled,
      max_enabled: project.max_enabled,
    }));
  }, [project]);

  // Берём origin админки. Виджет раздаётся с того же домена, что и админка.
  const origin = window.location.origin;
  const headSnippet = `<script src="${origin}/widget.js" data-project="${project.slug}" defer></script>`;
  const inlineSnippet = `<div data-ai-seller-inline data-project="${project.slug}" style="height:520px;max-width:520px;margin:0 auto;"></div>`;
  // Открыть чат из любой кнопки на сайте — три способа:
  const anchorSnippet = `#ai-seller`;
  const attrSnippet = `<button data-ai-seller-open>Запустить ИИ-подбор</button>\n<a href="#" data-ai-seller-open>Получить расчёт</a>`;
  const apiSnippet = `<script>
  AiSellerWidget.open();    // открыть
  AiSellerWidget.close();   // закрыть
  AiSellerWidget.toggle();  // переключить
</script>`;

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Скопировано в буфер обмена");
    } catch {
      toast.error("Не удалось скопировать");
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Сайт — кнопка чата в углу</CardTitle>
          <CardDescription>
            Подключается одной строкой и работает на всех страницах сайта.
            Самый простой способ.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2">
            <Switch
              id="ch-web"
              checked={form.web_enabled}
              onCheckedChange={(v) => setForm((s) => ({ ...s, web_enabled: Boolean(v) }))}
            />
            <Label htmlFor="ch-web">Канал «Сайт» включён</Label>
          </div>

          <div className="space-y-2">
            <Label>Шаг 1 — скопируй этот код:</Label>
            <div className="flex gap-2">
              <Input readOnly value={headSnippet} className="font-mono text-xs" />
              <Button variant="outline" size="icon" onClick={() => copy(headSnippet)}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="space-y-2 text-sm text-muted-foreground">
            <p className="font-medium text-foreground">Шаг 2 — куда вставить:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                <span className="font-medium text-foreground">Tilda:</span>{" "}
                «Настройки сайта» → «Ещё» → «HTML-код для вставки внутри тега{" "}
                <code className="text-xs bg-muted px-1 py-0.5 rounded">&lt;head&gt;</code>».
                Сохранить и опубликовать сайт.
              </li>
              <li>
                <span className="font-medium text-foreground">WordPress:</span>{" "}
                плагин «Insert Headers and Footers» → поле «Scripts in Header».
              </li>
              <li>
                <span className="font-medium text-foreground">Любой сайт с доступом к коду:</span>{" "}
                перед закрывающим тегом{" "}
                <code className="text-xs bg-muted px-1 py-0.5 rounded">&lt;/head&gt;</code>{" "}
                или{" "}
                <code className="text-xs bg-muted px-1 py-0.5 rounded">&lt;/body&gt;</code>.
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Сайт — встроить чат прямо в страницу</CardTitle>
          <CardDescription>
            Дополнительно к кнопке в углу. Чат отрисуется внутри блока вёрстки
            на конкретной странице (например, в разделе «Контакты» или
            «Связаться с нами»). Кнопка в углу при этом тоже останется.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Скопируй и вставь в HTML-блок страницы:</Label>
            <div className="flex gap-2">
              <Textarea readOnly value={inlineSnippet} className="font-mono text-xs h-20" />
              <Button variant="outline" size="icon" onClick={() => copy(inlineSnippet)}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Высоту и ширину блока подгони под свой дизайн через{" "}
              <code className="text-xs bg-muted px-1 py-0.5 rounded">style="height:..."</code>.
              Скрипт повторно подключать не нужно — он уже подключён в шапке сайта.
            </p>
          </div>

          <div className="space-y-2 text-sm text-muted-foreground">
            <p className="font-medium text-foreground">Куда вставить:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                <span className="font-medium text-foreground">Tilda Zero Block:</span>{" "}
                добавить элемент «HTML», вставить код.
              </li>
              <li>
                <span className="font-medium text-foreground">Tilda стандартные блоки:</span>{" "}
                блок «Другое» → T123 (HTML-код) → вставить код.
              </li>
              <li>
                <span className="font-medium text-foreground">WordPress / Битрикс:</span>{" "}
                блок «Произвольный HTML» в нужном месте страницы.
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Сайт — открывать чат из любой кнопки</CardTitle>
          <CardDescription>
            Любая кнопка на сайте (например, «Запустить ИИ-подбор», «Получить
            расчёт») может открыть окно чата в углу. Доступны три способа —
            выбирай самый удобный для конкретного места.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <Label>1. Самый простой для Tilda — ссылка-якорь</Label>
            <p className="text-xs text-muted-foreground">
              У любой кнопки в Tilda есть поле «Ссылка» (URL). Вставь туда
              этот текст и сохрани. Ничего больше делать не нужно — виджет
              перехватит клик и откроет чат в углу.
            </p>
            <div className="flex gap-2">
              <Input readOnly value={anchorSnippet} className="font-mono text-xs" />
              <Button variant="outline" size="icon" onClick={() => copy(anchorSnippet)}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label>2. Если кнопка вставлена через HTML-блок — атрибут</Label>
            <p className="text-xs text-muted-foreground">
              Добавь атрибут{" "}
              <code className="text-xs bg-muted px-1 py-0.5 rounded">data-ai-seller-open</code>{" "}
              на любой свой элемент:
            </p>
            <div className="flex gap-2">
              <Textarea readOnly value={attrSnippet} className="font-mono text-xs h-20" />
              <Button variant="outline" size="icon" onClick={() => copy(attrSnippet)}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label>3. JS-API — для программного открытия</Label>
            <p className="text-xs text-muted-foreground">
              Например, открыть чат после отправки формы или по таймауту.
              На странице доступен глобальный объект{" "}
              <code className="text-xs bg-muted px-1 py-0.5 rounded">window.AiSellerWidget</code>:
            </p>
            <div className="flex gap-2">
              <Textarea readOnly value={apiSnippet} className="font-mono text-xs h-28" />
              <Button variant="outline" size="icon" onClick={() => copy(apiSnippet)}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <Alert>
            <AlertDescription className="text-xs">
              Все три способа работают только если виджет уже подключён в шапке
              сайта (см. блок выше — «Кнопка в углу»).
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Telegram</CardTitle>
          <CardDescription>
            Получи токен у{" "}
            <a
              href="https://t.me/BotFather"
              className="underline"
              target="_blank"
              rel="noreferrer"
            >
              @BotFather
            </a>{" "}
            и вставь его сюда.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2">
            <Switch
              id="ch-tg"
              checked={form.telegram_enabled}
              onCheckedChange={(v) => setForm((s) => ({ ...s, telegram_enabled: Boolean(v) }))}
            />
            <Label htmlFor="ch-tg">Канал «Telegram» включён</Label>
          </div>
          <div className="space-y-2">
            <Label htmlFor="tg-token">Токен бота</Label>
            <Input
              id="tg-token"
              type="password"
              placeholder="Не показываем сохранённый токен. Введи новый, чтобы заменить."
              value={form.telegram_bot_token}
              onChange={(e) =>
                setForm((s) => ({ ...s, telegram_bot_token: e.target.value }))
              }
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>MAX</CardTitle>
          <CardDescription>Зарегистрируй бота в MAX и вставь токен.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2">
            <Switch
              id="ch-max"
              checked={form.max_enabled}
              onCheckedChange={(v) => setForm((s) => ({ ...s, max_enabled: Boolean(v) }))}
            />
            <Label htmlFor="ch-max">Канал «MAX» включён</Label>
          </div>
          <div className="space-y-2">
            <Label htmlFor="max-token">Токен бота</Label>
            <Input
              id="max-token"
              type="password"
              placeholder="Введи новый токен, чтобы заменить."
              value={form.max_bot_token}
              onChange={(e) => setForm((s) => ({ ...s, max_bot_token: e.target.value }))}
            />
          </div>
        </CardContent>
      </Card>

      <div>
        <Button
          onClick={() => {
            const payload: ProjectUpdate = {
              web_enabled: form.web_enabled,
              telegram_enabled: form.telegram_enabled,
              max_enabled: form.max_enabled,
            };
            if (form.telegram_bot_token.trim()) {
              payload.telegram_bot_token = form.telegram_bot_token.trim();
            }
            if (form.max_bot_token.trim()) {
              payload.max_bot_token = form.max_bot_token.trim();
            }
            mutation.mutate(payload);
            setForm((s) => ({ ...s, telegram_bot_token: "", max_bot_token: "" }));
          }}
          disabled={mutation.isPending}
        >
          {mutation.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          Сохранить настройки каналов
        </Button>
      </div>
    </div>
  );
}
